import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownWideNarrow, ArrowUpNarrowWide, Loader2, Pencil, Star, Users } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import api from '../services/api';
import { useToast } from './shared/Toast';
import ConfirmModal from './shared/ConfirmModal';
import { favoriteUnitPrice } from '../utils/adminPriceDisplay';
import { formatMoney } from '../utils/formatMoney';
import { LoadFailed } from './settings/instantSettings';
import { SheetField, SheetLocked } from './settings/sheet';
import {
  ChipTone, Column, DataTable, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, ICON_BTN, INPUT_CLS, KeyValue, KeyValueItem,
  ListPage, ListToolbar, PRIMARY_BTN, Pagination, Stat, Stats, StatusChip, useListPage,
} from './shared/listPage';

/**
 * «Πελάτες» (Προσωπικό) στη διάταξη «Πίνακας» (14/10/2026): ListPage + ListToolbar (αναζήτηση, VIP) + DataTable
 * (ταξινόμηση από τον server) + DetailPanel με το προφίλ — Πληροφορίες · Παραγγελίες · Διευθύνσεις · Αγαπημένα —
 * και τη φόρμα στοιχείων. Ίδια endpoints και ίδια σώματα με πριν:
 *   GET   /stores/:id/customers?page&limit=25&sort_by&sort_dir[&search][&vip=true]
 *   GET   /stores/:id/customers/:cid/profile
 *   PATCH /stores/:id/customers/:cid/vip   { is_vip }
 *   PATCH /stores/:id/customers/:cid       { name, email, phone_secondary, customer_comments, hidden_comments }
 * Μέσα στο παράθυρο «Πελάτες» του ταμείου (embedded) χωρίς κεφαλίδα: τον τίτλο τον έχει το παράθυρο.
 */

// ── Τύποι ──────────────────────────────────────────────────────

interface Customer {
  id: number;
  phone: string;
  phone_secondary?: string;
  name: string;
  email?: string;
  notes?: string;
  total_orders: number;
  total_spent: number;
  average_order_value: number;
  last_order_date?: string;
  first_order_date?: string;
  preferred_payment_method?: string;
  is_vip: boolean;
  loyalty_points: number;
  customer_comments?: string;
  hidden_comments?: string;
  company_name?: string;
  company_vat?: string;
  address_count: number;
  created_at: string;
  updated_at: string;
}

interface CustomerProfile extends Customer {
  addresses: CustomerAddress[];
  recent_orders: RecentOrder[];
  favorite_items: FavoriteItem[];
}

interface CustomerAddress {
  id: number;
  label?: string;
  address_type: string;
  street_address: string;
  area?: string;
  city?: string;
  postal_code?: string;
  floor?: string;
  doorbell?: string;
  delivery_notes?: string;
  is_default: boolean;
  times_used: number;
}

interface RecentOrder {
  id: number;
  order_date: string;
  total_amount: number;
  order_type: string;
  short_code: string;
  status: string;
  items_count: number;
  street_address?: string;
  area?: string;
  cancelled_at?: string | null;
  cancel_reason?: string | null;
  cancelled_by_first_name?: string | null;
  cancelled_by_last_name?: string | null;
}

interface FavoriteItem {
  product_name: string;
  size_name?: string;
  // F1-S1: true όταν το size_name είναι το ΜΟΝΑΔΙΚΟ πωλήσιμο μέγεθος (κανόνας του server,
  // utils/singleSizeName) — δεν γράφεται· λείπει σε backend πριν το F1-S1 (→ φαίνεται, όπως πριν).
  size_name_hidden?: boolean;
  // Τιμή delivery της γραμμής από τον server· null μόνο για ανωμαλία (→ 0).
  unit_price?: number | null;
  times_ordered: number;
}

interface PageInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface EditForm {
  name: string;
  email: string;
  phone_secondary: string;
  customer_comments: string;
  hidden_comments: string;
}

type LoadState = 'ready' | 'error' | 'forbidden';
type ProfileState = 'loading' | 'ready' | 'error';
type ProfileTab = 'info' | 'orders' | 'addresses' | 'favorites';
type SortDirection = 'asc' | 'desc';
/** Τι γίνεται μετά το «Απόρριψη αλλαγών»: κλείσιμο του συρταριού ή επιστροφή από τη φόρμα στις πληροφορίες. */
type DiscardAction = 'close' | 'cancelEdit';

// ── Βοηθοί ─────────────────────────────────────────────────────

const PAGE_SIZE = 25;
const EMPTY_PAGE: PageInfo = { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 };
const EMPTY_EDIT: EditForm = { name: '', email: '', phone_secondary: '', customer_comments: '', hidden_comments: '' };
// Η προεπιλεγμένη σειρά του server (πριν πατηθεί κάποια στήλη) — δεν είναι στήλη του πίνακα.
const DEFAULT_SORT = 'updated_at';

// Ημερομηνίες ελληνικές, ώρα Ελλάδας (όπως οι Κλήσεις και τα Ζ/Χ).
const ATHENS = 'Europe/Athens';
const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('el-GR', { timeZone: ATHENS, day: '2-digit', month: '2-digit', year: 'numeric' });
};

const editFormOf = (c: Customer): EditForm => ({
  name: c.name || '',
  email: c.email || '',
  phone_secondary: c.phone_secondary || '',
  customer_comments: c.customer_comments || '',
  hidden_comments: c.hidden_comments || '',
});

const sameForm = (a: EditForm, b: EditForm): boolean => (Object.keys(a) as Array<keyof EditForm>).every(k => a[k] === b[k]);

const httpStatus = (err: unknown): number | undefined => (err as { response?: { status?: number } } | null)?.response?.status;

// Κατάσταση παραγγελίας → τόνος ετικέτας (ως 14/10 ένα χρώμα ανά κατάσταση· τώρα μόνο ok/warn/danger/muted).
const ORDER_TONES: Record<string, ChipTone> = {
  completed: 'ok',
  delivered: 'ok',
  pending: 'warn',
  accepted: 'muted',
  ready: 'muted',
  dispatched: 'muted',
  rejected: 'danger',
  cancelled: 'danger',
};

/** Καρτέλες μέσα στο συρτάρι (ARIA tabs: βελάκια, Home/End). */
function PanelTabs<K extends string>({ tabs, active, onChange, label, idPrefix }: {
  tabs: Array<{ id: K; label: string; count?: number }>;
  active: K;
  onChange: (id: K) => void;
  label: string;
  idPrefix: string;
}): React.ReactElement {
  const refs = useRef<Partial<Record<K, HTMLButtonElement | null>>>({});
  const onKey = (event: React.KeyboardEvent, index: number) => {
    let next = -1;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    if (next < 0) return;
    event.preventDefault();
    onChange(tabs[next].id);
    refs.current[tabs[next].id]?.focus();
  };
  return (
    // Αναδίπλωση αντί για οριζόντια κύλιση: στο κινητό μια κρυμμένη δεξιά καρτέλα δεν τη μαντεύει κανείς.
    <div role="tablist" aria-label={label} className="flex flex-wrap gap-x-1 border-b border-line">
      {tabs.map((tab, i) => {
        const selected = tab.id === active;
        return (
          <button
            key={tab.id}
            ref={el => { refs.current[tab.id] = el; }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-tabpanel`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={e => onKey(e, i)}
            className={`relative inline-flex items-center gap-1.5 shrink-0 h-10 px-3 rounded-t-lg text-sm whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand ${selected
              ? 'text-brand font-semibold after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-brand'
              : 'text-t3 font-medium hover:text-t1'}`}
          >
            {tab.label}
            {typeof tab.count === 'number' && (
              <span className={`inline-grid place-items-center min-w-[18px] h-[18px] px-1.5 rounded-full text-[11px] font-semibold tabular-nums ${selected ? 'bg-brand-soft text-brand' : 'bg-surface-3 text-t3'}`}>
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Ταξινόμηση στο στενό πλάτος (κινητό): εκεί ο πίνακας γίνεται στοίβα χωρίς επικεφαλίδες, άρα και χωρίς
 * κλικ ταξινόμησης — όπως τα παλιά κουμπιά «Παραγγελίες»/«Έσοδα», ίδιοι κανόνες: νέα στήλη = φθίνουσα,
 * ξανά η ίδια = αντίστροφα. Στο φαρδύ/μεσαίο ταξινομούν οι επικεφαλίδες του πίνακα.
 */
const NarrowSort: React.FC<{ sortBy: string; sortDir: SortDirection; onSort: (field: string) => void; onReset: () => void }> = ({
  sortBy, sortDir, onSort, onReset,
}) => {
  const { t } = useTranslation(['customers']);
  const page = useListPage();
  if (page?.layout !== 'narrow') return null;
  const dirLabel = sortDir === 'desc' ? t('customers:sort.desc') : t('customers:sort.asc');
  return (
    <>
      <FilterSelect
        label={t('customers:sort.label')}
        value={sortBy === DEFAULT_SORT ? '' : sortBy}
        allLabel={t('customers:sort.recent')}
        options={[
          { value: 'total_orders', label: t('customers:sort.orders') },
          { value: 'total_spent', label: t('customers:sort.spent') },
          { value: 'last_order_date', label: t('customers:sort.lastOrder') },
        ]}
        onChange={v => (v ? onSort(v) : onReset())}
        testId="customers-sort"
      />
      {sortBy !== DEFAULT_SORT && (
        <button type="button" className={ICON_BTN} onClick={() => onSort(sortBy)} aria-label={dirLabel} title={dirLabel}>
          {sortDir === 'desc'
            ? <ArrowDownWideNarrow className="w-4 h-4" aria-hidden="true" />
            : <ArrowUpNarrowWide className="w-4 h-4" aria-hidden="true" />}
        </button>
      )}
    </>
  );
};

const SECTION_TITLE = 'text-sm font-semibold text-t1 mt-6 mb-3';
const TEXTAREA_CLS = `${INPUT_CLS} resize-none`;

// ── Σελίδα ─────────────────────────────────────────────────────

const CustomerManagement: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { t } = useTranslation(['customers', 'admin', 'common', 'settings', 'listPage']);
  const { store } = useAuth();
  const { showToast } = useToast();
  const storeId = store?.id;
  const uid = useId();
  const formId = `${uid}-form`;

  // Λίστα
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [pagination, setPagination] = useState<PageInfo>(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState<LoadState>('ready');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  const [filterVip, setFilterVip] = useState<string>('');
  // Αναζήτηση/ταξινόμηση στέλνουν νέο αίτημα: μετρά μόνο η απάντηση του τελευταίου.
  const listSeq = useRef(0);

  // Προφίλ (συρτάρι)
  const [selected, setSelected] = useState<Customer | null>(null);
  const [profile, setProfile] = useState<CustomerProfile | null>(null);
  const [profileState, setProfileState] = useState<ProfileState>('loading');
  const [profileTab, setProfileTab] = useState<ProfileTab>('info');
  const profileSeq = useRef(0);

  // Φόρμα στοιχείων
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState<EditForm>(EMPTY_EDIT);
  const [editBase, setEditBase] = useState<EditForm>(EMPTY_EDIT);
  const [nameError, setNameError] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState<DiscardAction | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const editDirty = isEditingInfo && !sameForm(editForm, editBase);

  // ── Λίστα πελατών ────────────────────────────────────────────

  const fetchCustomers = useCallback(async (page = 1) => {
    if (!storeId) return;
    const id = ++listSeq.current;
    setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: String(PAGE_SIZE),
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (searchTerm) params.search = searchTerm;
      if (filterVip) params.vip = filterVip;

      const res = await api.get(`/stores/${storeId}/customers`, { params });
      if (id !== listSeq.current) return;
      setCustomers(res.data.customers || []);
      setPagination(res.data.pagination || EMPTY_PAGE);
      setState('ready');
    } catch (err) {
      console.error('Failed to fetch customers:', err);
      if (id === listSeq.current) setState(httpStatus(err) === 403 ? 'forbidden' : 'error');
    } finally {
      if (id === listSeq.current) setLoading(false);
    }
  }, [storeId, sortBy, sortDir, searchTerm, filterVip]);

  useEffect(() => {
    fetchCustomers(1);
  }, [fetchCustomers]);

  // Η αναζήτηση φεύγει 400ms μετά το τελευταίο πλήκτρο (όπως πάντα).
  useEffect(() => {
    const timer = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Ίδιος κανόνας με τα παλιά κουμπιά: νέα στήλη = φθίνουσα, η ίδια ξανά = αντίστροφα.
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
  };
  const resetSort = () => {
    setSortBy(DEFAULT_SORT);
    setSortDir('desc');
  };

  // ── Προφίλ ───────────────────────────────────────────────────

  const loadProfile = async (customer: Customer) => {
    if (!storeId) return;
    const id = ++profileSeq.current;
    setProfileState('loading');
    try {
      const res = await api.get(`/stores/${storeId}/customers/${customer.id}/profile`);
      if (id !== profileSeq.current) return;
      const c = res.data.customer;
      setProfile({
        ...c,
        addresses: typeof c.addresses === 'string' ? JSON.parse(c.addresses) : (c.addresses || []),
        recent_orders: c.recent_orders || [],
        favorite_items: c.favorite_items || [],
      });
      setProfileState('ready');
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      if (id === profileSeq.current) setProfileState('error');
    }
  };

  const openProfile = (customer: Customer) => {
    setSelected(customer);
    setProfile(null);
    setProfileTab('info');
    setIsEditingInfo(false);
    setNameError(false);
    loadProfile(customer);
  };

  const closePanel = () => {
    profileSeq.current += 1; // ένα προφίλ που φορτώνει ακόμη δεν ξανανοίγει το συρτάρι
    setSelected(null);
    setProfile(null);
    setIsEditingInfo(false);
    setNameError(false);
    setConfirmDiscard(null);
  };

  // Esc / X / φόντο: πρώτα το παράθυρο επιβεβαίωσης· με αλλαγές στη φόρμα, ερώτηση.
  const requestClose = () => {
    if (confirmDiscard) {
      setConfirmDiscard(null);
      return;
    }
    if (savingEdit) return;
    if (editDirty) {
      setConfirmDiscard('close');
      return;
    }
    closePanel();
  };

  // ── VIP ──────────────────────────────────────────────────────

  const toggleVip = async (customerId: number, currentVip: boolean) => {
    if (!storeId) return;
    try {
      await api.patch(`/stores/${storeId}/customers/${customerId}/vip`, { is_vip: !currentVip });
      setCustomers(prev => prev.map(c => (c.id === customerId ? { ...c, is_vip: !currentVip } : c)));
      setSelected(prev => (prev && prev.id === customerId ? { ...prev, is_vip: !currentVip } : prev));
      setProfile(prev => (prev && prev.id === customerId ? { ...prev, is_vip: !currentVip } : prev));
    } catch (err) {
      console.error('Failed to toggle VIP:', err);
    }
  };

  // ── Φόρμα στοιχείων ──────────────────────────────────────────

  const startEditingInfo = () => {
    if (!profile) return;
    const base = editFormOf(profile);
    setEditForm(base);
    setEditBase(base);
    setNameError(false);
    setProfileTab('info');
    setIsEditingInfo(true);
  };

  const stopEditing = () => {
    setIsEditingInfo(false);
    setNameError(false);
    setConfirmDiscard(null);
  };

  const cancelEditingInfo = () => {
    if (savingEdit) return;
    if (editDirty) setConfirmDiscard('cancelEdit');
    else stopEditing();
  };

  const setField = (key: keyof EditForm, value: string) => {
    if (key === 'name') setNameError(false);
    setEditForm(prev => ({ ...prev, [key]: value }));
  };

  const saveEditingInfo = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!profile || !storeId || savingEdit) return;
    if (!editForm.name.trim()) {
      setNameError(true);
      nameRef.current?.focus();
      return;
    }
    setSavingEdit(true);
    try {
      const res = await api.patch(`/stores/${storeId}/customers/${profile.id}`, editForm);
      const updated = res.data.customer;
      setProfile(prev => (prev ? { ...prev, ...updated } : prev));
      setSelected(prev => (prev && prev.id === profile.id ? { ...prev, ...updated } : prev));
      setCustomers(prev => prev.map(c => (c.id === profile.id ? { ...c, ...updated } : c)));
      setIsEditingInfo(false);
      showToast('success', t('customers:toast.updated'));
    } catch (err: any) {
      console.error('Failed to update customer:', err);
      showToast('error', err?.response?.data?.error || t('customers:toast.updateFailed'));
    } finally {
      setSavingEdit(false);
    }
  };

  const confirmDiscardChanges = () => {
    const action = confirmDiscard;
    setConfirmDiscard(null);
    if (action === 'close') closePanel();
    else stopEditing();
  };

  // ── Κείμενα ──────────────────────────────────────────────────

  const unknown = t('admin:unknownCustomer');

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: t('admin:statusPendingLabel'),
      accepted: t('admin:statusAcceptedLabel'),
      ready: t('admin:statusReadyLabel'),
      dispatched: t('admin:statusDispatchedLabel'),
      delivered: t('admin:statusDeliveredLabel'),
      completed: t('admin:statusCompletedLabel'),
      rejected: t('admin:statusRejectedLabel'),
      cancelled: t('admin:statusCancelledLabel'),
    };
    return labels[status] || status;
  };

  const getOrderTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      delivery: t('common:orderType.delivery'),
      takeaway: t('common:orderType.takeaway'),
      dine_in: t('common:orderType.dineIn'),
      internal: t('admin:orderTypeInternal'),
    };
    return labels[type] || type;
  };

  const paymentLabel = (method: string) => t(`common:payment.${method}`, { defaultValue: method });

  // Συνάρτηση και όχι component: ένα component ορισμένο εδώ θα ξαναφτιαχνόταν σε κάθε ζωγραφιά (χάνει το focus).
  const vipButton = (customer: Customer) => {
    const name = customer.name || unknown;
    return (
      <button
        type="button"
        aria-pressed={customer.is_vip}
        aria-label={t('customers:vip.toggleLabel', { name })}
        title={customer.is_vip ? t('customers:vip.remove') : t('customers:vip.mark')}
        onClick={() => toggleVip(customer.id, customer.is_vip)}
        className={`inline-flex items-center gap-1 min-h-[32px] px-2 rounded-lg text-xs font-semibold transition-colors ${customer.is_vip
          ? 'bg-brand-soft text-brand'
          : 'text-t4 hover:text-t2 hover:bg-surface-3'}`}
      >
        <Star className={`w-3.5 h-3.5 shrink-0 ${customer.is_vip ? 'fill-current' : ''}`} aria-hidden="true" />
        {customer.is_vip && t('customers:vip.label')}
      </button>
    );
  };

  // ── Στήλες ───────────────────────────────────────────────────

  const columns: Column<Customer>[] = [
    {
      key: 'name',
      header: t('admin:customerColumn'),
      width: 'minmax(190px, 2fr)',
      stack: 'title',
      render: c => (
        <>
          <span className="block font-medium">{c.name || <span className="text-t3 font-normal">{unknown}</span>}</span>
          {c.company_name && <span className="block text-xs text-t3">{c.company_name}</span>}
        </>
      ),
      stackRender: c => (
        <>
          {c.name || unknown}
          {c.is_vip && <>{' '}<StatusChip tone="brand">{t('customers:vip.label')}</StatusChip></>}
        </>
      ),
    },
    {
      key: 'phone',
      header: t('common:phone'),
      width: 'minmax(150px, 1.3fr)',
      render: c => (
        <>
          <span className="block tabular-nums">{c.phone}</span>
          {c.email && <span className="block text-xs text-t3 truncate">{c.email}</span>}
        </>
      ),
      stackRender: c => c.phone,
    },
    {
      key: 'total_orders',
      header: t('admin:ordersHeaderColumn'),
      width: '140px',
      align: 'right',
      sortable: true,
      render: c => <span className="tabular-nums">{c.total_orders}</span>,
      stackRender: c => t('customers:ordersCount', { count: c.total_orders }),
    },
    {
      key: 'total_spent',
      header: t('admin:totalColumn'),
      width: '120px',
      align: 'right',
      sortable: true,
      stack: 'end',
      render: c => <span className="font-medium">{formatMoney(c.total_spent)}</span>,
      stackRender: c => formatMoney(c.total_spent),
    },
    {
      key: 'average_order_value',
      header: t('admin:avgColumn'),
      width: '104px',
      align: 'right',
      hideBelow: 'medium',
      render: c => <span className="text-t2">{formatMoney(c.average_order_value)}</span>,
    },
    {
      key: 'last_order_date',
      header: t('admin:lastOrderColumn'),
      width: '136px',
      sortable: true,
      render: c => <span className="text-t2 tabular-nums">{formatDate(c.last_order_date)}</span>,
      stackRender: c => (c.last_order_date ? t('admin:lastOrder', { date: formatDate(c.last_order_date) }) : null),
    },
    {
      key: 'is_vip',
      header: t('admin:vipColumn'),
      width: '90px',
      align: 'center',
      hideBelow: 'narrow',
      // Το γρήγορο VIP της γραμμής μένει (ένα πάτημα, όπως πριν)· το κλικ στη γραμμή ανοίγει το προφίλ.
      render: vipButton,
    },
  ];

  // ── Περιεχόμενο συρταριού ────────────────────────────────────

  const infoItems = (c: CustomerProfile): KeyValueItem[] => [
    { id: 'name', label: t('customers:fields.name'), value: c.name },
    { id: 'phone', label: t('customers:fields.phone'), value: <span className="tabular-nums">{c.phone}</span> },
    ...(c.phone_secondary ? [{ id: 'phone2', label: t('customers:fields.phone2'), value: <span className="tabular-nums">{c.phone_secondary}</span> }] : []),
    ...(c.email ? [{ id: 'email', label: t('common:email'), value: c.email }] : []),
    { id: 'first', label: t('customers:fields.firstOrder'), value: c.first_order_date ? formatDate(c.first_order_date) : null },
    { id: 'last', label: t('customers:fields.lastOrder'), value: c.last_order_date ? formatDate(c.last_order_date) : null },
    ...(c.preferred_payment_method ? [{ id: 'payment', label: t('customers:fields.payment'), value: paymentLabel(c.preferred_payment_method) }] : []),
  ];

  const companyItems = (c: CustomerProfile): KeyValueItem[] => [
    ...(c.company_name ? [{ id: 'company', label: t('customers:fields.companyName'), value: c.company_name }] : []),
    ...(c.company_vat ? [{ id: 'vat', label: t('customers:fields.companyVat'), value: <span className="tabular-nums">{c.company_vat}</span> }] : []),
  ];

  const noteItems = (c: CustomerProfile): KeyValueItem[] => [
    ...(c.customer_comments ? [{ id: 'comments', label: t('customers:fields.customerComments'), value: c.customer_comments }] : []),
    ...(c.hidden_comments ? [{ id: 'hidden', label: t('customers:fields.internalComments'), value: c.hidden_comments }] : []),
    ...(c.notes ? [{ id: 'notes', label: t('customers:fields.notes'), value: c.notes }] : []),
  ];

  const renderInfo = (c: CustomerProfile) => {
    const company = companyItems(c);
    const notes = noteItems(c);
    return (
      <div data-testid="customer-info">
        <KeyValue items={infoItems(c)} testId="customer-info-values" />
        {company.length > 0 && (
          <>
            <h3 className={SECTION_TITLE}>{t('customers:sections.company')}</h3>
            <KeyValue items={company} />
          </>
        )}
        {notes.length > 0 && (
          <>
            <h3 className={SECTION_TITLE}>{t('customers:sections.notes')}</h3>
            <KeyValue items={notes} />
          </>
        )}
      </div>
    );
  };

  const renderEditForm = (c: CustomerProfile) => (
    <form id={formId} onSubmit={saveEditingInfo} noValidate className="space-y-4" data-testid="customer-edit-form">
      <div className="xs-grid2">
        <SheetField label={t('customers:fields.nameRequired')} htmlFor={`${uid}-name`}>
          <input
            id={`${uid}-name`}
            ref={nameRef}
            type="text"
            value={editForm.name}
            autoComplete="off"
            onChange={e => setField('name', e.target.value)}
            aria-invalid={nameError || undefined}
            aria-describedby={nameError ? `${uid}-name-error` : undefined}
            className={INPUT_CLS}
          />
          {nameError && <p id={`${uid}-name-error`} className="mt-1.5 text-xs text-red-600 dark:text-red-400">{t('admin:nameRequired')}</p>}
        </SheetField>
        {/* Το κύριο τηλέφωνο είναι η ταυτότητα του πελάτη: δεν αλλάζει από εδώ. */}
        <SheetLocked label={t('customers:fields.phone')} value={c.phone} hint={t('customers:fields.phoneLockedHint')} />
      </div>
      <div className="xs-grid2">
        <SheetField label={t('customers:fields.phone2')} htmlFor={`${uid}-phone2`}>
          <input id={`${uid}-phone2`} type="text" inputMode="tel" value={editForm.phone_secondary} autoComplete="off"
            onChange={e => setField('phone_secondary', e.target.value)} className={`${INPUT_CLS} tabular-nums`} />
        </SheetField>
        <SheetField label={t('common:email')} htmlFor={`${uid}-email`}>
          <input id={`${uid}-email`} type="email" value={editForm.email} autoComplete="off"
            onChange={e => setField('email', e.target.value)} className={INPUT_CLS} />
        </SheetField>
      </div>
      <SheetField label={t('customers:fields.customerComments')} htmlFor={`${uid}-comments`}>
        <textarea id={`${uid}-comments`} rows={2} value={editForm.customer_comments}
          onChange={e => setField('customer_comments', e.target.value)} className={TEXTAREA_CLS} />
      </SheetField>
      <SheetField label={t('customers:fields.internalComments')} htmlFor={`${uid}-hidden`}>
        <textarea id={`${uid}-hidden`} rows={2} value={editForm.hidden_comments}
          onChange={e => setField('hidden_comments', e.target.value)} className={TEXTAREA_CLS} />
      </SheetField>
    </form>
  );

  const renderOrders = (c: CustomerProfile) => (c.recent_orders.length === 0 ? (
    <EmptyState title={t('admin:noRecentOrders')} testId="customer-orders-empty" />
  ) : (
    <ul className="divide-y divide-line" data-testid="customer-orders">
      {c.recent_orders.map(order => {
        const by = [order.cancelled_by_first_name, order.cancelled_by_last_name].filter(Boolean).join(' ');
        return (
          <li key={order.id} className="py-3 first:pt-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-t1 tabular-nums">#{order.short_code}</span>
              <StatusChip tone={ORDER_TONES[order.status] || 'muted'}>{getStatusLabel(order.status)}</StatusChip>
              <span className="ml-auto text-sm font-semibold text-t1 tabular-nums">{formatMoney(order.total_amount)}</span>
            </div>
            <p className="mt-0.5 text-xs text-t3">
              {formatDate(order.order_date)} · {getOrderTypeLabel(order.order_type)} · {t('admin:orderItemsCount', { count: order.items_count })}
            </p>
            {order.street_address && (
              <p className="mt-0.5 text-xs text-t3">{order.street_address}{order.area ? `, ${order.area}` : ''}</p>
            )}
            {order.status === 'cancelled' && (
              <p className="mt-1 text-xs text-red-700 dark:text-red-400">
                <span className="font-semibold">{t('customers:orders.cancelled')}</span>
                {order.cancelled_at ? ` · ${formatDate(order.cancelled_at)}` : ''}
                {by ? ` · ${t('customers:orders.cancelledBy', { name: by })}` : ''}
                {order.cancel_reason ? <> · <span className="italic">«{order.cancel_reason}»</span></> : null}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  ));

  const renderAddresses = (c: CustomerProfile) => (c.addresses.length === 0 ? (
    <EmptyState title={t('admin:noAddresses')} testId="customer-addresses-empty" />
  ) : (
    <ul className="divide-y divide-line" data-testid="customer-addresses">
      {c.addresses.map(addr => (
        <li key={addr.id} className="py-3 first:pt-0 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="flex flex-wrap items-center gap-2 text-sm font-medium text-t1">
              <span className="min-w-0">{addr.street_address}</span>
              {addr.is_default && <StatusChip tone="brand">{t('admin:defaultAddressLabel')}</StatusChip>}
            </p>
            {[addr.area, addr.city, addr.postal_code].some(Boolean) && (
              <p className="mt-0.5 text-xs text-t3">{[addr.area, addr.city, addr.postal_code].filter(Boolean).join(', ')}</p>
            )}
            {(addr.floor || addr.doorbell) && (
              <p className="mt-0.5 text-xs text-t3">
                {addr.floor && t('admin:floorLabel', { value: addr.floor })}
                {addr.floor && addr.doorbell && ' · '}
                {addr.doorbell && t('admin:doorbellLabel', { value: addr.doorbell })}
              </p>
            )}
            {addr.delivery_notes && <p className="mt-0.5 text-xs text-t3 italic">{addr.delivery_notes}</p>}
          </div>
          {addr.times_used > 0 && (
            <span className="shrink-0 text-xs text-t3 tabular-nums" title={t('customers:addresses.timesUsed', { count: addr.times_used })}>
              {addr.times_used}×
            </span>
          )}
        </li>
      ))}
    </ul>
  ));

  const renderFavorites = (c: CustomerProfile) => (c.favorite_items.length === 0 ? (
    <EmptyState title={t('admin:noFavorites')} testId="customer-favorites-empty" />
  ) : (
    <ul className="divide-y divide-line" data-testid="customer-favorites">
      {c.favorite_items.map((item, idx) => (
        // eslint-disable-next-line react/no-array-index-key -- τα αγαπημένα δεν έχουν id (ίδιο κλειδί με πριν)
        <li key={idx} className="py-3 first:pt-0 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-t1">{item.product_name}</p>
            <p className="mt-0.5 text-xs text-t3 tabular-nums">
              {item.size_name && !item.size_name_hidden && <span>{item.size_name} · </span>}
              {formatMoney(favoriteUnitPrice(item))}
            </p>
          </div>
          <span className="shrink-0 text-sm font-medium text-t2 tabular-nums" title={t('customers:favorites.timesOrdered', { count: item.times_ordered })}>
            {item.times_ordered}×
          </span>
        </li>
      ))}
    </ul>
  ));

  const renderProfile = () => {
    if (profileState === 'error') {
      return <LoadFailed message={t('customers:profileLoadFailed')} onRetry={() => selected && loadProfile(selected)} />;
    }
    if (profileState === 'loading' || !profile) {
      return (
        <div className="space-y-3 pt-1" data-testid="customer-profile-loading">
          {[62, 40, 78, 52, 68].map((w, i) => <span key={i} className="lp-skel" style={{ width: `${w}%` }} />)}
          <span className="sr-only" role="status">{t('admin:loadingProfile')}</span>
        </div>
      );
    }
    const tabs: Array<{ id: ProfileTab; label: string; count?: number }> = [
      { id: 'info', label: t('admin:infoTab') },
      // Αριθμός μόνο στις διευθύνσεις (είναι όλες)· οι παραγγελίες/τα αγαπημένα είναι μόνο τα πρόσφατα/τα
      // πρώτα του server, και ένας αριθμός δίπλα τους θα διαβαζόταν ως σύνολο.
      { id: 'orders', label: t('admin:ordersTab') },
      { id: 'addresses', label: t('admin:addressesTab'), count: profile.addresses.length },
      { id: 'favorites', label: t('admin:favoritesTab') },
    ];
    return (
      <>
        <Stats testId="customer-stats">
          <Stat label={t('customers:stats.orders')} value={profile.total_orders} />
          <Stat label={t('customers:stats.spent')} value={formatMoney(profile.total_spent)} />
          <Stat label={t('customers:stats.average')} value={formatMoney(profile.average_order_value)} />
          <Stat label={t('customers:stats.points')} value={profile.loyalty_points} />
        </Stats>
        <div className="mt-4">
          <PanelTabs tabs={tabs} active={profileTab} onChange={setProfileTab} label={t('customers:panel.tabs')} idPrefix={uid} />
        </div>
        <div id={`${uid}-tabpanel`} role="tabpanel" aria-labelledby={`${uid}-tab-${profileTab}`} className="pt-4">
          {profileTab === 'info' && (isEditingInfo ? renderEditForm(profile) : renderInfo(profile))}
          {profileTab === 'orders' && renderOrders(profile)}
          {profileTab === 'addresses' && renderAddresses(profile)}
          {profileTab === 'favorites' && renderFavorites(profile)}
        </div>
      </>
    );
  };

  const panelCustomer: Customer | null = profile || selected;
  const panelName = panelCustomer ? (panelCustomer.name || unknown) : '';
  const panelReady = profileState === 'ready' && !!profile;

  const panelFooter = isEditingInfo ? (
    <>
      <button type="button" className={GHOST_BTN} onClick={cancelEditingInfo} disabled={savingEdit}>{t('common:cancel')}</button>
      <button type="submit" form={formId} className={PRIMARY_BTN} disabled={savingEdit} aria-busy={savingEdit || undefined}>
        {savingEdit && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
        {savingEdit ? t('common:saving') : t('common:save')}
      </button>
    </>
  ) : (
    <>
      <button type="button" className={GHOST_BTN} onClick={requestClose}>{t('listPage:close')}</button>
      {panelReady && (
        <button type="button" className={PRIMARY_BTN} onClick={startEditingInfo}>
          <Pencil className="w-4 h-4" aria-hidden="true" />
          {t('customers:panel.edit')}
        </button>
      )}
    </>
  );

  const panelFooterStart = !isEditingInfo && panelReady && profile ? (
    <button
      type="button"
      className={GHOST_BTN}
      aria-pressed={profile.is_vip}
      onClick={() => toggleVip(profile.id, profile.is_vip)}
    >
      <Star className={`w-4 h-4 ${profile.is_vip ? 'fill-current text-brand' : ''}`} aria-hidden="true" />
      {profile.is_vip ? t('customers:vip.remove') : t('customers:vip.mark')}
    </button>
  ) : null;

  const filtersActive = !!(searchTerm || filterVip);
  const firstLoad = loading && customers.length === 0;

  const page = (
    <ListPage
      embedded={embedded}
      eyebrow={t('settings:nav.groups.people')}
      title={t('settings:nav.customers')}
      subtitle={t('customers:subtitle')}
      testId="customers-page"
    >
      <ListToolbar
        search={{
          value: searchInput,
          onChange: setSearchInput,
          placeholder: t('admin:searchCustomers'),
          ariaLabel: t('customers:searchLabel'),
        }}
        filters={(
          <>
            <FilterSelect
              label={t('customers:vip.filterLabel')}
              value={filterVip}
              onChange={setFilterVip}
              allLabel={t('customers:vip.all')}
              options={[{ value: 'true', label: t('customers:vip.only') }]}
              testId="customers-vip-filter"
            />
            <NarrowSort sortBy={sortBy} sortDir={sortDir} onSort={handleSort} onReset={resetSort} />
          </>
        )}
        summary={state === 'ready' && !firstLoad ? t('customers:count', { count: pagination.total }) : null}
      />

      {state !== 'ready' ? (
        <LoadFailed
          message={state === 'forbidden' ? t('customers:forbidden') : t('customers:loadFailed')}
          onRetry={() => fetchCustomers(pagination.page || 1)}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={customers}
          rowKey={c => c.id}
          onRowClick={openProfile}
          selectedKey={selected?.id ?? null}
          loading={loading}
          stickyHeader={!embedded}
          sort={{ key: sortBy, dir: sortDir }}
          onSortChange={s => handleSort(s.key)}
          rowLabel={c => t('customers:rowLabel', { name: c.name || unknown, phone: c.phone })}
          testId="customers-table"
          empty={(
            <EmptyState
              icon={Users}
              title={filtersActive ? t('admin:noCustomersFound') : t('admin:noCustomersYet')}
              hint={filtersActive ? t('customers:empty.filteredHint') : undefined}
            />
          )}
          footer={(
            <Pagination
              page={pagination.page}
              pageSize={pagination.limit || PAGE_SIZE}
              total={pagination.total}
              onPage={p => fetchCustomers(p)}
              disabled={loading}
            />
          )}
        />
      )}

      <DetailPanel
        open={selected !== null}
        onClose={requestClose}
        title={(
          <>
            {panelName}
            {panelCustomer?.is_vip && <>{' '}<StatusChip tone="brand">{t('customers:vip.label')}</StatusChip></>}
          </>
        )}
        subtitle={panelCustomer ? [panelCustomer.phone, panelCustomer.email].filter(Boolean).join(' · ') : undefined}
        footer={panelFooter}
        footerStart={panelFooterStart}
        testId="customer-panel"
      >
        {selected && renderProfile()}
        {/* Μέσα στο συρτάρι (στη ρίζα του χώρου εργασίας): έξω του, μέσα στο παράθυρο «Πελάτες» του ταμείου,
            θα ζωγραφιζόταν κάτω από το συρτάρι. */}
        <ConfirmModal
          isOpen={confirmDiscard !== null}
          title={t('customers:discard.title')}
          message={t('customers:discard.message')}
          confirmLabel={t('customers:discard.confirm')}
          cancelLabel={t('customers:discard.cancel')}
          variant="warning"
          onConfirm={confirmDiscardChanges}
          onCancel={() => setConfirmDiscard(null)}
        />
      </DetailPanel>
    </ListPage>
  );

  // Μέσα στο παράθυρο του ταμείου το περιεχόμενο δεν έχει δικό του περιθώριο (το παράθυρο δεν δίνει).
  return embedded ? <div className="p-4 sm:p-5">{page}</div> : page;
};

export default CustomerManagement;
